import './header.scss';
import clsx from 'clsx';
import { NavLink, Link, useNavigate, useLocation } from 'react-router';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { Menu } from 'primereact/menu';
import type { MenuItem, MenuItemCommandEvent } from 'primereact/menuitem';
import { useTgStore } from '@/stores/tg';
import { useUserStore } from '@/stores/user';

interface HeaderProps {
  withBorder: boolean; // will show border-bottom with shadow
  hideAuth?: boolean;
}

function Header({ withBorder = false, hideAuth = false }: HeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);
  const isSuperuser = useUserStore((s) => s.isSuperuser);
  const authName = useUserStore((s) => s.authName);
  const avatarUrl = useUserStore((s) => s.avatarUrl);
  const logout = useUserStore((s) => s.logout);

  const isTgAIPage = typeof window !== 'undefined' && location.pathname.startsWith('/ai/answers/');
  const isLandingPage = typeof window !== 'undefined' && location.pathname === '/landing';
  const shouldHideAuth = hideAuth || isLandingPage;
  // during a meeting, navigation links open in a new tab so the call isn't left
  const isMeetingPage =
    typeof window !== 'undefined' && /^\/(meeting|lesson)\//.test(location.pathname);
  const newTabProps = isMeetingPage
    ? { target: '_blank', rel: 'noreferrer', onClick: () => setIsMenuOpen(false) }
    : {};

  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const profileMenuRef = useRef<Menu>(null);
  const { tgTheme } = useTgStore();

  useEffect(() => {
    if (isTgAIPage) {
      setTheme(tgTheme);
    }
  }, [tgTheme]);

  // the mobile menu closes on navigation, outside click and Escape
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMenuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!navRef.current?.contains(e.target as Node)) setIsMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isMenuOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const profileMenuItems: MenuItem[] = [
    {
      label: 'Личный кабинет',
      icon: 'pi pi-user',
      // a real href, so "open in new tab" opens the profile and not the current page
      url: '/profile',
      target: isMeetingPage ? '_blank' : undefined,
      command: ({ originalEvent: e }) => {
        if (isMeetingPage) return;
        const mouse = e as MouseEvent;
        if (mouse.ctrlKey || mouse.metaKey || mouse.shiftKey) return;
        e.preventDefault();
        navigate('/profile');
      }
    },
    ...(isSuperuser
      ? [
          {
            label: 'Админка',
            icon: 'pi pi-shield',
            url: '/admin',
            target: isMeetingPage ? '_blank' : undefined,
            command: (event: MenuItemCommandEvent) => {
              if (isMeetingPage) return;
              const mouse = event.originalEvent as MouseEvent;
              if (mouse?.ctrlKey || mouse?.metaKey || mouse?.shiftKey) return;
              mouse?.preventDefault();
              navigate('/admin');
            }
          }
        ]
      : []),
    {
      label: 'Выход из аккаунта',
      icon: 'pi pi-sign-out',
      className: 'header__profile-menu-danger',
      command: handleLogout
    }
  ];

  return (
    <nav
      ref={navRef}
      className={clsx(
        'header',
        withBorder && 'header_border-bottom',
        isMenuOpen && 'header_menu-open',
        theme
      )}
    >
      <div className="header__content">
        <div className="header__left">
          <button
            type="button"
            className={clsx(
              'header__menu-hamburger',
              isMenuOpen && 'header__menu-hamburger_open',
              theme
            )}
            aria-label={isMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
            aria-expanded={isMenuOpen}
            aria-controls="header-links"
            onClick={() => setIsMenuOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>
          <h3 className={clsx('header__main-logo', theme)}>
            <img className="header__main-logo-img" src="/logo.png" alt="logo" />
            {shouldHideAuth ? (
              <a href="/">Repeteka</a>
            ) : (
              <NavLink to={!isTgAIPage ? '/' : '#'} {...newTabProps}>
                Repeteka
              </NavLink>
            )}
          </h3>
          <div
            id="header-links"
            className={clsx('header__links', isMenuOpen && 'header__links_open', theme)}
          >
            <NavLink
              className={clsx('header__link', theme)}
              to={'https://t.me/repeteka_bot'}
              target="_blank"
              rel="noreferrer"
              aria-label="Repeteka bot"
              title="Repeteka bot"
            >
              <img className="header__link-img" src="/meeting_icons/tg_bot.svg" alt="" />
              <span className={clsx('header__link-text', theme)}>Repeteka bot</span>
            </NavLink>
            {isAuthenticated && !isTgAIPage && (
              <>
                <NavLink
                  className={clsx('header__link header__link_account', theme)}
                  to="/groups"
                  {...newTabProps}
                  aria-label="Учебные группы"
                  title="Учебные группы"
                >
                  <i className={clsx('pi pi-users header__link-icon', theme)} aria-hidden />
                  <span className={clsx('header__link-text', theme)}>Учебные группы</span>
                </NavLink>
                <NavLink
                  className={clsx('header__link header__link_account', theme)}
                  to="/history"
                  {...newTabProps}
                  aria-label="История занятий"
                  title="История занятий"
                >
                  <i className={clsx('pi pi-history header__link-icon', theme)} aria-hidden />
                  <span className={clsx('header__link-text', theme)}>История занятий</span>
                </NavLink>
                {isSuperuser && (
                  <NavLink
                    className={clsx('header__link header__link_account', theme)}
                    to="/admin"
                    {...newTabProps}
                    aria-label="Админка"
                    title="Админка"
                  >
                    <i className={clsx('pi pi-shield header__link-icon', theme)} aria-hidden />
                    <span className={clsx('header__link-text', theme)}>Админка</span>
                  </NavLink>
                )}
              </>
            )}
          </div>
        </div>
        {!isTgAIPage && (
          <div className="header__right">
            {isAuthenticated ? (
              <div className="header__profile">
                <button
                  type="button"
                  className="header__profile-link"
                  aria-label="Меню профиля"
                  aria-haspopup="menu"
                  onClick={(e) => profileMenuRef.current?.toggle(e)}
                >
                  <Avatar
                    image={avatarUrl || undefined}
                    label={(authName || '?').charAt(0).toUpperCase()}
                    shape="circle"
                  />
                  <span className="header__profile-name">{authName}</span>
                </button>
                <Menu
                  model={profileMenuItems}
                  popup
                  popupAlignment="right"
                  ref={profileMenuRef}
                  className="header__profile-menu"
                />
              </div>
            ) : (
              !shouldHideAuth && (
                <div className="header__auth">
                  <Link to="/login" className="header__auth-login">
                    <Button
                      className="header__auth-button header__auth-button_login"
                      link
                      size="small"
                    >
                      <span className="text">Вход</span>
                      <i className="pi pi-sign-in icon" />
                    </Button>
                  </Link>
                  <Link to="/register" className="header__auth-register">
                    <Button className="header__auth-button" size="small">
                      <span className="text">Регистрация</span>
                      <i className="pi pi-user-plus icon" />
                    </Button>
                  </Link>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </nav>
  );
}

export default Header;
